/**
 * Camera (doc 17 §2). Pure logic, no renderer — so it can be unit-tested and so
 * the zoom rules cannot quietly become framework behaviour.
 *
 * Two invariants hold everywhere:
 *   1. Zoom is an INTEGER scale S in {2,3,4}. Non-integer zoom resamples pixels,
 *      which is the one thing this whole project is built to avoid.
 *   2. The camera origin is an integer internal pixel, so a world texel always
 *      lands on exactly S x S screen pixels.
 */

import { TH, TW } from './iso.ts';

export const ZOOM_LEVELS = [2, 3, 4] as const;
export type Zoom = (typeof ZOOM_LEVELS)[number];
export const DEFAULT_ZOOM: Zoom = 2;

/** Doc 17 §2: the map plus this margin stays reachable. */
const EDGE_MARGIN = 64;

export interface Viewport {
  /** Window size in CSS px. */
  cssW: number;
  cssH: number;
}

export interface WorldBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Screen-space extent of a cols x rows iso grid whose tile (0,0) sits at the origin. */
export function gridBounds(cols: number, rows: number): WorldBounds {
  return {
    minX: -(rows - 1) * TW - TW,
    maxX: (cols - 1) * TW + TW,
    minY: -TH,
    maxY: (cols - 1 + rows - 1) * TH + TH * 2,
  };
}

export class Camera {
  /** Internal-pixel coordinate shown at the viewport's top-left. Always integer. */
  x = 0;
  y = 0;
  zoom: Zoom = DEFAULT_ZOOM;
  bounds: WorldBounds;

  constructor(bounds: WorldBounds) {
    this.bounds = bounds;
  }

  /** Viewport size in INTERNAL pixels at the current zoom. */
  viewInternal(v: Viewport): { w: number; h: number } {
    return { w: Math.ceil(v.cssW / this.zoom), h: Math.ceil(v.cssH / this.zoom) };
  }

  centerOn(wx: number, wy: number, v: Viewport): void {
    const view = this.viewInternal(v);
    this.x = Math.round(wx - view.w / 2);
    this.y = Math.round(wy - view.h / 2);
    this.clamp(v);
  }

  /** Pan by a delta in SCREEN px (mouse drag, edge scroll). */
  panScreen(dxScreen: number, dyScreen: number, v: Viewport): void {
    this.x = Math.round(this.x + dxScreen / this.zoom);
    this.y = Math.round(this.y + dyScreen / this.zoom);
    this.clamp(v);
  }

  /** Pan by a delta in INTERNAL px (keyboard, per-tick scroll speed). */
  panWorld(dx: number, dy: number, v: Viewport): void {
    this.x = Math.round(this.x + dx);
    this.y = Math.round(this.y + dy);
    this.clamp(v);
  }

  /**
   * Zoom one step, keeping the world point under (screenX, screenY) fixed.
   * Snaps instantly between levels — an interpolated scale would resample.
   */
  zoomAt(step: 1 | -1, screenX: number, screenY: number, v: Viewport): boolean {
    const i = ZOOM_LEVELS.indexOf(this.zoom);
    const next = ZOOM_LEVELS[i + step];
    if (next === undefined) return false;
    const worldX = this.x + screenX / this.zoom;
    const worldY = this.y + screenY / this.zoom;
    this.zoom = next;
    this.x = Math.round(worldX - screenX / next);
    this.y = Math.round(worldY - screenY / next);
    this.clamp(v);
    return true;
  }

  setZoom(z: Zoom, v: Viewport): void {
    const view = this.viewInternal(v);
    const cx = this.x + view.w / 2;
    const cy = this.y + view.h / 2;
    this.zoom = z;
    this.centerOn(cx, cy, v);
  }

  clamp(v: Viewport): void {
    const view = this.viewInternal(v);
    const b = this.bounds;
    const spanX = b.maxX - b.minX + EDGE_MARGIN * 2;
    const spanY = b.maxY - b.minY + EDGE_MARGIN * 2;
    if (view.w >= spanX) {
      // Whole map fits: centre it rather than letting it drift to a corner.
      this.x = Math.round((b.minX + b.maxX) / 2 - view.w / 2);
    } else {
      const lo = b.minX - EDGE_MARGIN;
      const hi = b.maxX + EDGE_MARGIN - view.w;
      this.x = Math.round(Math.min(hi, Math.max(lo, this.x)));
    }
    if (view.h >= spanY) {
      this.y = Math.round((b.minY + b.maxY) / 2 - view.h / 2);
    } else {
      const lo = b.minY - EDGE_MARGIN;
      const hi = b.maxY + EDGE_MARGIN - view.h;
      this.y = Math.round(Math.min(hi, Math.max(lo, this.y)));
    }
  }

  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    return { x: this.x + sx / this.zoom, y: this.y + sy / this.zoom };
  }

  worldToScreen(wx: number, wy: number): { x: number; y: number } {
    return { x: (wx - this.x) * this.zoom, y: (wy - this.y) * this.zoom };
  }

  /** Screen rect of the visible world, in internal px — the culling rect (doc 12). */
  visibleRect(v: Viewport, margin = 64): { x0: number; y0: number; x1: number; y1: number } {
    const view = this.viewInternal(v);
    return {
      x0: this.x - margin,
      y0: this.y - margin,
      x1: this.x + view.w + margin,
      y1: this.y + view.h + margin,
    };
  }
}

/**
 * UI scale (doc 17 §1). Independent of world zoom on purpose: this is the whole
 * point of the two-layer model — zooming the world never resizes UI text.
 */
export type UiScale = 1 | 2 | 3;
export type UiScalePref = 'auto' | 'small' | 'normal' | 'large';

/**
 * Doc 17 §1 proposed `round(winH / 1080)`, which puts almost every real window
 * at U = 1 — and stage 0's first browser run showed exactly what that means: an
 * 11 px line height at native resolution is unreadable, which is the opposite
 * of why the two-layer model exists. Anchored on absolute legibility instead:
 * body text never renders below 22 screen px of line box.
 */
export function autoUiScale(cssH: number): UiScale {
  if (cssH < 700) return 1;
  if (cssH < 1600) return 2;
  return 3;
}

export function uiScaleFor(cssH: number, pref: UiScalePref = 'auto'): UiScale {
  const auto = autoUiScale(cssH);
  switch (pref) {
    case 'small':
      return Math.max(1, auto - 1) as UiScale;
    case 'normal':
      return auto;
    case 'large':
      return Math.min(3, auto + 1) as UiScale;
    default:
      return auto;
  }
}

/** Doc 17 §1: below this the game letterboxes rather than reflow. */
export const MIN_WINDOW = { w: 1280, h: 720 } as const;
